import { Check, Sparkles } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";

type PricingCardsProps = {
  heading?: string;
  subheading?: string;
  showStandard?: boolean;
  showSpring?: boolean;
  showReferral?: boolean;
};

const PricingCards = ({
  heading = "Deux prix, un seul outil.",
  subheading = "Commence par l'essai gratuit. Choisis le tarif qui te correspond.",
  showStandard = true,
  showSpring = true,
  showReferral = false,
}: PricingCardsProps) => {
  const navigate = useNavigate();

  return (
    <section id="tarifs" className="py-24 bg-gradient-hero">
      <div className="container mx-auto px-4">
        <div className="text-center mb-14">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-foreground mb-4">
            {heading}
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {subheading}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {showStandard && (
            <Card className="relative border-border bg-card hover:shadow-lg transition-shadow">
              <CardContent className="p-8 space-y-6">
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">
                    Offre Standard
                  </p>
                  <p className="text-sm text-muted-foreground">
                    30 jours gratuits, puis
                  </p>
                </div>

                <div className="flex items-baseline gap-2">
                  <span className="font-playfair text-6xl font-bold text-foreground">49</span>
                  <span className="text-muted-foreground text-lg">CHF / mois</span>
                </div>

                <p className="text-base text-foreground leading-relaxed">
                  Accessible à tous les restaurateurs suisses. Toutes les fonctionnalités, sans limite, sans commission par couvert.
                </p>

                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Réservations illimitées, tables illimitées, clients illimités</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Rappels automatiques, widget de réservation, plan de salle</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Statistiques, multi-staff, PWA mobile</span>
                  </li>
                </ul>

                <Button
                  size="lg"
                  className="w-full"
                  onClick={() => navigate("/signup")}
                >
                  Commencer l'essai gratuit
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  Sans carte bancaire, sans engagement
                </p>
              </CardContent>
            </Card>
          )}

          {showSpring && (
            <Card className="relative border-2 border-primary bg-card shadow-lg">
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground">
                <Sparkles className="w-3 h-3 mr-1" />
                50 premiers restaurants
              </Badge>
              <CardContent className="p-8 space-y-6">
                <div>
                  <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-2">
                    Offre de Printemps 2026
                  </p>
                  <p className="text-sm text-muted-foreground">
                    60 jours gratuits, verrouillé à vie
                  </p>
                </div>

                <div className="flex items-baseline gap-2">
                  <span className="font-playfair text-6xl font-bold text-primary">29</span>
                  <span className="text-muted-foreground text-lg">CHF / mois, à vie</span>
                </div>

                <p className="text-base text-foreground leading-relaxed">
                  Réservée aux <strong>50 premiers restaurants</strong> qui rejoignent Piktable avant le <strong>30 septembre 2026</strong>.
                </p>

                <div className="border-t border-border pt-4 space-y-2">
                  <p className="text-xs uppercase tracking-widest text-primary font-semibold">
                    Bonus Fondateur
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Tarif verrouillé à vie. Jamais d'augmentation.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Logo « Restaurant Fondateur » sur piktable.ch</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Accès prioritaire aux nouvelles fonctionnalités</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>1 h de coaching à la mise en ligne</span>
                    </li>
                  </ul>
                </div>

                <Button
                  size="lg"
                  className="w-full"
                  onClick={() => navigate("/signup")}
                >
                  Rejoindre les Fondateurs
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  Sans carte bancaire, sans engagement
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {showReferral && (
          <div className="mt-12 max-w-3xl mx-auto">
            <Card className="border-border bg-card">
              <CardContent className="p-6 md:p-8 space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1">
                      Offre Parrainage
                    </p>
                    <h3 className="font-playfair text-xl font-semibold text-foreground">
                      Descends ton tarif jusqu'à 29 CHF / mois à vie
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Présente des restaurateurs qui prennent rendez-vous avec nous. Ton tarif baisse automatiquement et définitivement.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                  <div className="bg-muted/50 rounded-lg px-4 py-3 text-center">
                    <p className="text-xs text-muted-foreground">1 parrainage</p>
                    <p className="font-playfair text-lg font-bold text-foreground">39 CHF / mois</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg px-4 py-3 text-center">
                    <p className="text-xs text-muted-foreground">2 parrainages</p>
                    <p className="font-playfair text-lg font-bold text-foreground">34 CHF / mois</p>
                  </div>
                  <div className="bg-primary/10 border border-primary/30 rounded-lg px-4 py-3 text-center">
                    <p className="text-xs text-primary font-semibold">3 parrainages</p>
                    <p className="font-playfair text-lg font-bold text-primary">29 CHF / mois à vie</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </section>
  );
};

export default PricingCards;
